import React from 'react';
import { Button, Dropdown, Space, Tooltip } from 'antd';
import { MoreOutlined } from '@ant-design/icons';
import useResponsive from '../../hooks/useResponsive';

/**
 * Responsive page-header action bar.
 *
 * Desktop (>640px): shows every button with its label.
 * Mobile (≤640px): secondary buttons become icon-only with a tooltip.
 * Tiny (≤480px): all secondary actions collapse into a single ••• menu;
 *                the primary action keeps its label so it stays obvious.
 *
 * Props:
 *   primary   — { label, icon, onClick, type='primary', danger?, style?, loading? }
 *   secondary — Array<{ key, label, icon, onClick, danger?, divider? }>
 *   size      — AntD button size (default 'middle')
 */
const HeaderActions = ({ primary, secondary = [], size = 'middle' }) => {
  const { isMobile, isTiny } = useResponsive();

  const renderSecondary = () => {
    if (isTiny && secondary.length > 0) {
      const items = secondary
        .filter((s) => !s.divider)
        .map((s) => ({
          key: s.key,
          icon: s.icon,
          label: s.label,
          danger: s.danger,
          onClick: s.onClick
        }));
      return (
        <Dropdown menu={{ items }} trigger={['click']}>
          <Button
            size={size}
            icon={<MoreOutlined />}
            aria-label="More actions"
          />
        </Dropdown>
      );
    }

    return secondary.map((s) => {
      if (s.divider) return null;
      if (isMobile) {
        return (
          <Tooltip key={s.key} title={s.label}>
            <Button
              size={size}
              icon={s.icon}
              danger={s.danger}
              onClick={s.onClick}
              aria-label={s.label}
            />
          </Tooltip>
        );
      }
      return (
        <Button
          key={s.key}
          size={size}
          icon={s.icon}
          danger={s.danger}
          onClick={s.onClick}
        >
          {s.label}
        </Button>
      );
    });
  };

  const renderPrimary = () => {
    if (!primary) return null;
    return (
      <Button
        size={size}
        type={primary.type || 'primary'}
        icon={primary.icon}
        danger={primary.danger}
        loading={primary.loading}
        onClick={primary.onClick}
        style={primary.style}
      >
        {primary.label}
      </Button>
    );
  };

  return (
    <Space size="small" wrap>
      {renderSecondary()}
      {renderPrimary()}
    </Space>
  );
};

export default HeaderActions;
